import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { equal, isValidNumber } from 'src/Library/Helper';
import { FormFieldConfig } from 'src/Library/Types';

export const getDate = (date, format = 'DD MMMM YYYY', ending = '') => {
  if (!date) return '-';
  return `${moment(date).format(format)}${ending}`;
};

export const getBidTotal = (amount) => {
  if (amount !== null && amount !== undefined && !isNaN(amount)) {
    return Number(amount.toFixed(2));
  }
  return -1;
};

export const prettifyInquiryMode = {
  FCL: 'FCL',
  LCL: 'LCL',
  AIRCARGO: 'Air Cargo',
  TRAILER: 'Trailer',
  DOMESTIC: 'Domestic',
  RPTL: 'RPTL',
};

export const getUnitByMode = (mode) => {
  if (mode === 'FCL') return 'Container';
  if (mode === 'LCL') return 'cbm';
  if (mode === 'TRAILER') return 'Trailer';
  if (mode === 'DOMESTIC') return '';
  if (mode === 'RPTL') return 'cbm'; // RPTL uses LCL charge units
  return 'kg';
};

// it used while rendering as selectable options
export const inquiryModes = [
  { label: 'FCL', value: 'FCL' },
  { label: 'LCL', value: 'LCL' },
  { label: 'Air', value: 'AIRCARGO' },
  { label: 'Trailer', value: 'TRAILER' },
  { label: 'Domestic', value: 'DOMESTIC'},
  { label: 'Road', value: 'DOMESTIC'},
  { label: 'RPTL', value: 'RPTL'}
];

export const inquiryAccessType = [
  { label: 'PUBLIC', value: 'Public' },
  { label: 'PRIVATE', value: 'Only Me' },
  { label: 'TEAM', value: 'Team' },
];

export const defaultOriginPorts = [
  {
    code: 'INMUN',
    countryCode: 'IND',
    countryName: 'India',
    name: 'Mundra',
    type: 'WET',
  },
];

export const defaultDestinationPorts = [
  {
    code: 'DEHAM',
    countryCode: 'DEU',
    countryName: 'Germany',
    name: 'Hamburg',
    type: 'WET',
  },
];

export const connectionRequestMessage = 'No response from FF.';

export enum BookingSource {
  DIRECT_BOOKING = 'DIRECT_BOOKING',
  DEFAULT = 'DEFAULT',
  SPOT_BOOKING = 'SPOT_BOOKING',
}

export enum InquirySource {
  SINGLE = 'SINGLE',
  BULK = 'BULK',
  SPOT_SEARCH = 'SPOT_SEARCH',
  DIRECT_BOOKING = 'DIRECT_BOOKING',
  SPOT_SEARCH_ZERO_RESULTS = 'SPOT_SEARCH_ZERO_RESULTS',
}

export enum ConnectionType {
  SPOT_CONNECTION = 'SPOT_CONNECTION',
  ENTERPRISE = 'ENTERPRISE',
}

export enum CargoType {
  GENERAL = 'GENERAL',
  HAZ = 'HAZ',
  REEFER = 'REEFER',
}

export enum InquiryModes {
  FCL = 'FCL',
  LCL = 'LCL',
  AIRCARGO = 'AIRCARGO',
  TRAILER = 'TRAILER',
  DOMESTIC = 'DOMESTIC',
  RPTL = 'RPTL',
}

export enum InquiryStatus {
  CLOSED = 'CLOSED',
  CONFIRMED = 'CONFIRMED',
  OPEN = 'OPEN',
}

export enum LocationPreferences {
  FACTORY = 'Factory',
  CFS = 'CFS',
}

export interface BookingStats {
  confirmed: number;
  open: number;
  declined: number;
  cancelled: number;
}

export const hasExistingBookings = (bookingStats?: BookingStats): boolean => {
  if (!bookingStats) return false;
  const { confirmed, open, declined, cancelled } = bookingStats;
  return (confirmed + open + declined + cancelled) > 0;
};

export const inquiryOptionsMap = {
  confirm: [
    {
      key: 'KEEP_INQUIRY_OPEN',
      label: 'Keep inquiry open to receive bids',
    },
    {
      key: 'INQUIRY_CLOSED',
      label: 'Close inquiry to stop receiving bids',
    },
  ],
  reject: [
    {
      key: 'KEEP_INQUIRY_CLOSED',
      label: 'Keep inquiry closed',
    },
    {
      key: 'INQUIRY_REOPENED',
      label: 'Re-open inquiry to receive bids',
    },
  ],
};

export const disableInquiryClosingDateUtil = (current: any) => {
  let closingDate =
    current &&
    (moment(current).format('HH') <= moment().add(1, 'hours').format('HH')
      ? current < moment().endOf('day')
      : current < moment().startOf('day'));

  return closingDate;
};

export const disableInquiryClosingTimeUtil = (current: any) => {
  if (current > moment().endOf('day')) return {};

  let hour = Number(moment().add(1, 'hours').format('HH'));
  let disabledHours: number[] = [];
  for (let i = hour; i >= 0; i--) {
    disabledHours.push(i);
  }
  return {
    disabledMinutes: () => [],
    disabledSeconds: () => [],
    disabledHours: () => disabledHours,
  };
};

export const getDeadlineData = (deadlineData, timezone?: string) => {
  // Convert UTC epoch timestamp to organization timezone for display
  const deadlineMoment = timezone && deadlineData
    ? momentTimezone(deadlineData).tz(timezone)
    : deadlineData ? moment(deadlineData) : null;
  
  const deadline = deadlineMoment 
    ? `${deadlineMoment.format('DD MMMM YYYY | HH:mm')} Hrs` 
    : '-- : --';
  
  let deadlineString = deadline;
  let deadlineColor = '#424242';
  let isDeadlineNear = false;
  
  // For time comparisons, use UTC to ensure accuracy
  if (deadlineData && moment(deadlineData) >= moment()) {
    if (moment(deadlineData) < moment().add(2, 'days')) {
      isDeadlineNear = true;
      const now = moment();
      const closingDate = moment(deadlineData);
      const difference = moment.duration(closingDate.diff(now));
      const hours = difference.asHours().toFixed(0);
      deadlineString = `${hours} hours left`;
      if (parseInt(hours, 10) <= 1) {
        const minutes = difference.asMinutes().toFixed(0);
        deadlineString = `${minutes} minutes left`;
      }
      deadlineColor = '#F5222D';
    }
  }
  return { deadline, deadlineString, deadlineColor, isDeadlineNear };
};

export const rejectInquiryShown = (bidsSaved, bidsSent, isRejected) => {
  let bidSavedValue = Number(bidsSaved);
  let bidSentValue = Number(bidsSent);
  return !bidSavedValue && !bidSentValue && !isRejected;
};

export const getMovementType = (origin, destination) => {
  let movementType;
  if (origin?.movementType && destination?.movementType) {
    movementType = 'Door to Door';
  } else if (origin?.movementType) {
    movementType = 'Door to Port';
  } else if (destination?.movementType) {
    movementType = 'Port to Door';
  }
  return movementType;
};

export const roundTripType = [
  { key: 'days', label: 'Days' },
  { key: 'hours', label: 'Hours' },
];

export const termsConditionsPlaceholder = `Type your own terms & conditions as below
1. Commodity: Food Products. Container should be neat and clean
2. Rail Freight: To be deducted from our PDA with Concor & With Adani/GDL (Shipper PDA)
3. Credit: One month from the date of invoice`;

export const productTempValidation = (rule, value, cb, reeferProductTempRange, product) => {
  if (!isValidNumber(value)) {
    return cb('Required');
  } else if (value > reeferProductTempRange[product]?.max) {
    return cb(`Maximum ${reeferProductTempRange[product]?.max} allowed`);
  } else if (value < reeferProductTempRange[product]?.min) {
    return cb(`Minimum ${reeferProductTempRange[product]?.min} required`);
  } else return cb();
};

export const visibleAccessTypeLabel = {
  PUBLIC: 'Workplace',
  PRIVATE: 'Only Me',
  TEAM: 'Team',
};

export const visibleAccessTypeLabelsForTeamMapped = {
  TEAM: 'Team',
  PRIVATE: 'Only Me'
}

export const defaultAccessKeys = ['PUBLIC', 'PRIVATE', 'TEAM'];
export const teamMappedAccessKeys = ['TEAM', 'PRIVATE']

export const modeWiseDefaultCarrierCodeKey = {
  [InquiryModes.LCL]: 'addBidDefaultCarrierCodeLCL',
  [InquiryModes.AIRCARGO]: 'addBidDefaultCarrierCodeAIRCARGO',
}

export const DEFAULT_PRODUCT_TYPE = 'General Dry Goods';

export enum BID_ROUTES {
  DIRECT = 'Direct',
  TRANSSHIPMENT_VIA = 'Transshipment Via',
}

export enum BID_ROUTES_CARD_LABELS {
  DIRECT = 'Direct',
  TRANSSHIPMENT_VIA = 'Transshipment',
}

export enum BULK_INQUIRY_COLORS {
  GREEN = '#52C41A',
  RED = '#F5222D',
  BLUE = '#1890FF',
  GREY = '#D9D9D9',
}


export const getDomesticCharges = (chargeObj, isExchangeDatePresent, currencySymbol = '') => {
  if (!Object.keys(chargeObj || {})?.length) return 0;
  
  if (isExchangeDatePresent) {
    if (currencySymbol) {
      const amount = chargeObj?.userTotalWithoutTax?.amount?.toFixed(2);
      return `${currencySymbol} ${amount}`;
    } else {
      return `${chargeObj?.userTotalWithoutTax?.amount?.toFixed(2)} ${chargeObj?.userTotalWithoutTax?.currency}`
    }
  }

  if (currencySymbol) {
    const amount = chargeObj?.shipperTotalWithoutTax?.amount?.toFixed(2);
    return `${currencySymbol} ${amount}`;
  }
  return `${chargeObj?.shipperTotalWithoutTax?.amount?.toFixed(2)} ${chargeObj?.shipperTotalWithoutTax?.currency}`
}

export const getPolPodTypesInquiryFilter = [
  {key: 'port', label: 'Port'},
  {key: 'country', label: 'Country'},
  {key: 'city', label: 'City'},
]

export type ProductContainerRuleSet = {
    cargoType?: string;
    productCnType?: string;
    cargoProductType?: string;
}

export type ProductVehicleRuleSet = ProductContainerRuleSet;

export const INQUIRY_FORM_GRID_ROWS = 20
export const INQUIRY_FORM_GRID_COLS = 2

export type InquiryFieldLabelMap = Record<string, string>;

export const buildInquiryFieldLabelMap = (configs?: FormFieldConfig[]): InquiryFieldLabelMap => {
  if (!configs) return {};
  return configs.reduce((acc, cur: FormFieldConfig) => {
    if (cur && cur.id && cur.customName) acc[cur.id] = cur.customName;
    return acc;
  }, {} as InquiryFieldLabelMap);
};

export const resolveFieldLabel = (id: string, defaultLabel: string, labelMap?: InquiryFieldLabelMap) =>
  (labelMap && labelMap[id]) ? labelMap[id] : defaultLabel;

export const formFieldConfig: Record<string, FormFieldConfig[]> = {
    [InquiryModes.FCL]: [
      {
        id: "grid#stakeholder",
        posY: 0,
        posX: 0,
      },
      {
        id: "grid#customer",
        posY: 0,
        posX: 1
      },
      {
        id: "grid#originPort",
        posY: 1,
        posX: 0
      }, 
      {
        id: "grid#destinationPort",
        posY: 1,
        posX: 1,
      },
      {
        id: "grid#cargoAndProduct",
        posY: 2,
        posX: 0,
        colspan: 1,
      },
      {
        id: "grid#containerDetails",
        posY: 3,
        posX: 0,
        colspan: 2
      },
      {
        id: "grid#productDetails",
        posY: 4,
        posX: 0,
        colspan: 2
      },
      {
        id: "grid#defaultTnC",
        posY: 5,
        posX: 0,
        colspan: 2
      },
      {
        id: "grid#cargoReadyDate",
        posY: 6,
        posX: 0
      },
      {
        id: "grid#internalReference",
        posY: 6,
        posX: 1
      },
      {
        id: "grid#preferredSailingRange",
        posY: 7,
        posX: 0
      }, 
      {
        id: "grid#inquiryClosingDate",
        posY: 7,
        posX: 1
      },
      {
        id: "grid#inquiryCustomFields",
        posY: 8,
        posX: 0,
      },
      {
        id:'grid.additionalDetails',
        posY: 9,
        posX: 0,
        colspan: 2
      },
      // {
      //   id:'grid.additionalDetails.movementType',
      //   posY: 9,
      //   posX: 0,
      //   colspan: 1
      // }, 
      // {
      //   id:'grid.additionalDetails.detailsAndServices',
      //   posY: 3,
      //   posX: 0, 
      //   colspan: 2
      // },
      // {
      //   id:'grid.additionalDetails.destinationDetailsAndServices',
      //   posY: 3,
      //   posX: 1, 
      //   colspan: 1
      // }
      
    ],
    [InquiryModes.LCL]: [
      {
        id: "grid#stakeholder",
        posY: 0,
        posX: 0,
      },
      {
        id: "grid#customer",
        posY: 0,
        posX: 1
      },
      {
        id: "grid#originPort",
        posY: 1,
        posX: 0
      }, 
      {
        id: "grid#destinationPort",
        posY: 1,
        posX: 1,
      },
      {
        id: "grid#cargoAndProduct",
        posY: 2,
        posX: 0,
        colspan: 1
      },
      {
        id: "grid#stackable",
        posY: 2,
        posX: 1,
        colspan: 1
      },
      {
        id: "grid#packagingDetails",
        posY: 3,
        posX: 0,
        colspan: 2
      },
      {
        id: "grid#productDetails",
        posY: 4,
        posX: 0,
        colspan: 2
      },
      {
        id: "grid#defaultTnC",
        posY: 5,
        posX: 0,
        colspan: 2
      },
      {
        id: "grid#cargoReadyDate",
        posY: 6,
        posX: 0
      },
      {
        id: "grid#internalReference",
        posY: 6,
        posX: 1
      },
      {
        id: "grid#preferredSailingRange",
        posY: 7,
        posX: 0
      }, 
      {
        id: "grid#inquiryClosingDate",
        posY: 7,
        posX: 1
      },
      {
        id: "grid#inquiryCustomFields",
        posY: 8,
        posX: 0,
      },
      {
        id:'grid.additionalDetails',
        posY: INQUIRY_FORM_GRID_ROWS - 1,
        posX: 0,
        colspan: 2
      },
      // {
      //   id:'grid.additionalDetails.movementType',
      //   posY: 9,
      //   posX: 0,
      //   colspan: 1
      // }, 
      // {
      //   id:'grid.additionalDetails.detailsAndServices',
      //   posY: 3,
      //   posX: 0, 
      //   colspan: 2
      // },
      // {
      //   id:'grid.additionalDetails.destinationDetailsAndServices',
      //   posY: 3,
      //   posX: 1, 
      //   colspan: 1
      // }
      
    ],
    [InquiryModes.AIRCARGO]: [
      {
        id: "grid#stakeholder",
        posY: 0,
        posX: 0,
      },
      {
        id: "grid#customer",
        posY: 0,
        posX: 1
      },
      {
        id: "grid#originPort",
        posY: 1,
        posX: 0
      }, 
      {
        id: "grid#destinationPort",
        posY: 1,
        posX: 1,
      },
      {
        id: "grid#cargoAndProduct",
        posY: 2,
        posX: 0,
        colspan: 1
      },
      {
        id: "grid#stackable",
        posY: 2,
        posX: 1,
        colspan: 1
      },
      {
        id: "grid#packagingDetails",
        posY: 3,
        posX: 0,
        colspan: 2
      },
      {
        id: "grid#productDetails",
        posY: 4,
        posX: 0,
        colspan: 2
      },
      {
        id: "grid#defaultTnC",
        posY: 5,
        posX: 0,
        colspan: 2
      },
      {
        id: "grid#cargoReadyDate",
        posY: 6,
        posX: 0
      },
      {
        id: "grid#internalReference",
        posY: 6,
        posX: 1
      },
      {
        id: "grid#preferredSailingRange",
        posY: 7,
        posX: 0
      }, 
      {
        id: "grid#inquiryClosingDate",
        posY: 7,
        posX: 1
      },
      {
        id: "grid#inquiryCustomFields",
        posY: 8,
        posX: 0,
      },
      {
        id:'grid.additionalDetails',
        posY: INQUIRY_FORM_GRID_ROWS - 1,
        posX: 0,
        colspan: 2
      },
      // {
      //   id:'grid.additionalDetails.movementType',
      //   posY: 9,
      //   posX: 0,
      //   colspan: 1
      // }, 
      // {
      //   id:'grid.additionalDetails.detailsAndServices',
      //   posY: 3,
      //   posX: 0, 
      //   colspan: 2
      // },
      // {
      //   id:'grid.additionalDetails.destinationDetailsAndServices',
      //   posY: 3,
      //   posX: 1, 
      //   colspan: 1
      // }
      
    ],
  }

export const masterVehicleTypes = {
  [`STANDARD_TRAILER`]: 'Standard Trailer',
  [`REEFER_TRAILER`]: 'Reefer Trailer',
  [`OPEN_TRAILER`]: 'Open Trailer',
  [`SEMI_TRAILER`]: 'Semi Trailer',
  [`FLATBED_TRAILER`]: 'Flatbed Trailer',
  [`TANKER_TRAILER`]: 'Tanker Trailer',
  [`BOX_TRAILER`]: 'Box Trailer',
  [`OPEN_BODY_VEHICLE`]: 'Open Body Vehicle',
  [`CLOSE_BODY_VEHICLE`]: 'Close Body Vehicle',
  [`INTERMODAL`]: 'Intermodal',
  [`TATA_ACE`]: 'Tata Ace',
};

export const masterContainerTypes = {
  STANDARD: 'Standard',
  ISO_TANKS: 'ISO Tanks',
  REFRIGERATED: 'Refrigerated',
  OPEN_TOP: 'Open Top',
  FLAT_RACK: 'Flat Rack',
  HEAVY_DUTY: 'Heavy Duty',
  FLEXI: 'Flexi',
};

export const getFclContainerAndQuantityDetails = (containerAndQuantityDetails: any[]): any[] => {
  if (!Array.isArray(containerAndQuantityDetails)) return [];
  const containerTypeKeys = Object.keys(masterContainerTypes);
  return containerAndQuantityDetails.filter(
    (detail) => containerTypeKeys.includes(detail?.containerType)
  );
};

/**
 * ISS-288612 §5: select a product's container/quantity rows for the chosen dispatch
 * mode. Mirrors the backend `selectByDispatchMode` precedence: per
 * (containerType, containerSize) group, a row whose `dispatchMode` matches the
 * selected mode wins; if the group has no mode-specific row, the default row
 * (`dispatchMode` null/undefined) is used. When no dispatch mode is selected,
 * the list is returned unchanged (legacy behaviour). This is how picking a
 * product + LOOSE surfaces the loose quantities and switching to PALLET surfaces
 * the pallet quantities.
 */
export const selectContainerDetailsByDispatchMode = (
  containerAndQuantityDetails: any[],
  dispatchMode?: string | null
): any[] => {
  if (!Array.isArray(containerAndQuantityDetails)) return [];
  if (!dispatchMode) return containerAndQuantityDetails;

  const groups: { [groupKey: string]: any[] } = {};
  containerAndQuantityDetails.forEach((detail) => {
    if (!detail) return; // skip null/undefined elements so they never leak into the result
    const groupKey = `${detail.containerType ?? ''}|${detail.containerSize ?? ''}`;
    if (!groups[groupKey]) groups[groupKey] = [];
    groups[groupKey].push(detail);
  });

  const result: any[] = [];
  Object.keys(groups).forEach((groupKey) => {
    const group = groups[groupKey];
    const exact = group.filter((detail) => detail?.dispatchMode === dispatchMode);
    if (exact.length) {
      result.push(...exact);
    } else {
      result.push(...group.filter((detail) => detail?.dispatchMode == null));
    }
  });
  return result;
};

export const cleanInchargeDetails = (inchargeDetails): string => {
  if (!inchargeDetails) return '';
  if (Array.isArray(inchargeDetails)) {
    return inchargeDetails
      .map((contact) => {
        const { name, phone, email } = contact || {};
        return `${name || ''} ${phone || ''} ${email || ''}`.trim();
      })
      .filter((contact) => contact !== '')
      .join('; ');
  }
  if (typeof inchargeDetails !== 'string') {
    return '';
  }

  // Remove whitespace, tabs, newlines, and filter out 'null' strings
  const cleaned = inchargeDetails
    .split(/\s+/)
    .filter((part) => part.trim() !== '' && part.toLowerCase() !== 'null')
    .join(' ')
    .trim();

  return cleaned || '';
};
